import React, { useState, useEffect } from 'react';
import { Card, Steps, Button, Form, DatePicker, InputNumber, Radio, Table, message, Input, Upload, Modal, Space, Timeline, Empty, Badge, Tooltip, Popover, Progress, Alert } from 'antd';
import { QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { examService } from '../../services/examService';
import moment from 'moment';
import StudentDetail from '../../pages/Students/components/StudentDetail';

// Step 1: Moved outside main component
const ExamSelectionStep = ({ value, onChange }) => (
  <Card>
    <Radio.Group 
      value={value} 
      onChange={e => onChange(e.target.value)}
      size="large"
    >
      {examService.examTypes.map(exam => (
        <Radio.Button key={exam.key} value={exam.key}>
          {exam.label}
        </Radio.Button>
      ))}
    </Radio.Group>
  </Card>
);

// Step 2: Moved outside main component
const DateSelectionStep = ({ bookingData, onBookingDataChange }) => (
  <Card>
    <Form layout="vertical">
      <Form.Item label="考试日期" required>
        <DatePicker
          value={bookingData.date}
          onChange={date => onBookingDataChange({ ...bookingData, date })}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label="考试人数" required>
        <InputNumber
          min={1}
          max={100}
          value={bookingData.studentCount}
          onChange={count => onBookingDataChange({ ...bookingData, studentCount: count })}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label="考官" required>
        <Input
          value={bookingData.examiner}
          onChange={e => onBookingDataChange({ ...bookingData, examiner: e.target.value })}
          placeholder="请输入考官姓名"
        />
      </Form.Item>
    </Form>
  </Card>
);

// Step 3: Modified to include search and student management
const StudentSelectionStep = ({ 
  bookingData, 
  selectedStudents, 
  onSelectedStudentsChange, 
  qualifiedStudents 
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searching, setSearching] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [currentNotes, setCurrentNotes] = useState([]);
  const [currentStudentName, setCurrentStudentName] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [allStudentsModalVisible, setAllStudentsModalVisible] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [loadingAllStudents, setLoadingAllStudents] = useState(false);
  const [filterQualified, setFilterQualified] = useState('all');
  const [allStudentsSearchValue, setAllStudentsSearchValue] = useState('');

  const handleSearch = async () => {
    if (!searchValue) {
      message.warning('请输入学员信息');
      return;
    }
    try {
      setSearching(true);
      const students = await examService.searchStudent(searchValue);
      setSearchResults(students);
      
      if (students.length === 0) {
        message.info('未找到符合条件的学员');
      } else {
        message.success(`找到 ${students.length} 名学员`);
      }
    } catch (error) {
      message.error('搜索学员失败');
    } finally {
      setSearching(false);
    }
  };

  const handleAddStudent = (student) => {
    // Check if student is already selected
    if (selectedStudents.some(s => s.objectId === student.objectId)) {
      message.warning('该学员已在名单中');
      return;
    }

    // Check qualification
    const { qualified, errors } = examService.checkStudentQualification(
      student, 
      bookingData.exam, 
      bookingData.date
    );

    if (!qualified) {
      Modal.error({
        title: '学员不符合考试要求',
        content: (
          <div>
            <p>无法添加学员，原因如下：</p>
            <ul>
              {errors.map((error, index) => (
                <li key={index} style={{ color: '#ff4d4f' }}>{error}</li>
              ))}
            </ul>
          </div>
        ),
      });
      return;
    }

    // Add student if qualified
    onSelectedStudentsChange([...selectedStudents, student]);
    setSearchModalVisible(false);
    setSearchValue('');
    setSearchResults([]);
  };

  const handleRemoveStudent = (studentId) => {
    onSelectedStudentsChange(selectedStudents.filter(s => s.objectId !== studentId));
  };

  const handleViewNotes = (student) => {
    setCurrentNotes(student.notes || []);
    setCurrentStudentName(student.name);
    setNotesModalVisible(true);
  };

  const handleViewDetail = (student) => {
    setCurrentStudent(student);
    setDetailModalVisible(true);
  };

  const handleImport = async (file) => {
    try {
      if (file.type !== 'text/plain') {
        message.error('请上传文本文件');
        return false;
      }

      setImporting(true);
      setImportProgress(0);

      const students = await examService.parseTextToStudents(file, (progress) => {
        setImportProgress(progress);
      });
      
      if (students.length === 0) {
        message.warning('未找到任何学员');
        return false;
      }

      // Check qualifications and filter students
      const results = students.map(student => ({
        student,
        ...examService.checkStudentQualification(student, bookingData.exam, bookingData.date)
      }));

      // Log qualification check results
      console.log('Qualification check results:', results);

      // Filter qualified students and those not already in the list
      const qualifiedStudents = results
        .filter(result => result.qualified)
        .filter(result => !selectedStudents.some(s => s.objectId === result.student.objectId))
        .map(result => result.student);

      // Prepare messages for different scenarios
      const unqualifiedCount = results.filter(r => !r.qualified).length;
      const duplicateCount = results.filter(r => 
        r.qualified && selectedStudents.some(s => s.objectId === r.student.objectId)
      ).length;

      // Add qualified students
      if (qualifiedStudents.length > 0) {
        onSelectedStudentsChange([...selectedStudents, ...qualifiedStudents]);
      }

      // Show result message
      Modal.info({
        title: '导入结果',
        content: (
          <div>
            <p>总计: {students.length} 名学员</p>
            <p>成功添加: {qualifiedStudents.length} 名</p>
            {unqualifiedCount > 0 && (
              <p>不符合要求: {unqualifiedCount} 名</p>
            )}
            {duplicateCount > 0 && (
              <p>已在名单中: {duplicateCount} 名</p>
            )}
            {results.filter(r => !r.qualified).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p>不符合要求的学员：</p>
                <ul>
                  {results.filter(r => !r.qualified).map((result, index) => (
                    <li key={index}>
                      {result.student.name} ({result.student.uniqueId}):
                      <ul>
                        {result.errors.map((error, errIndex) => (
                          <li key={errIndex} style={{ color: '#ff4d4f' }}>{error}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ),
      });

    } catch (error) {
      console.error('Import error:', error);
      message.error('导入失败');
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
    return false;
  };

  const fetchAllStudents = async () => {
    if (!bookingData.exam || !bookingData.date) {
      message.warning('请先选择考试科目和日期');
      return;
    }

    try {
      setLoadingAllStudents(true);
      message.loading({
        content: `正在获取可参加${bookingData.exam}考试的学员，请稍候...`,
        key: 'fetchStudentsLoading',
        duration: 0
      });
      
      const students = await examService.getAllStudentsWithQualification(bookingData.exam, bookingData.date);
      setAllStudents(students);
      setAllStudentsModalVisible(true);
      
      message.success({
        content: `成功获取 ${students.length} 名学员数据`,
        key: 'fetchStudentsLoading',
        duration: 2
      });
    } catch (error) {
      console.error('获取学员列表失败:', error);
      message.error({
        content: '获取学员列表失败',
        key: 'fetchStudentsLoading',
        duration: 2
      });
    } finally {
      setLoadingAllStudents(false);
    }
  };

  const handleAddStudentFromList = (student) => {
    // Check if student is already selected
    if (selectedStudents.some(s => s.objectId === student.objectId)) {
      message.warning('该学员已在名单中');
      return;
    }

    // Add student if qualified
    if (student.qualified) {
      onSelectedStudentsChange([...selectedStudents, student]);
      message.success(`已添加学员 ${student.name}`);
    } else {
      Modal.error({
        title: '学员不符合考试要求',
        content: (
          <div>
            <p>无法添加学员，原因如下：</p>
            <ul>
              {student.errors.map((error, index) => (
                <li key={index} style={{ color: '#ff4d4f' }}>{error}</li>
              ))}
            </ul>
          </div>
        ),
      });
    }
  };

  // Add a new method to filter the students list
  const getFilteredStudents = () => {
    // First apply qualification filter
    let filtered = 
      filterQualified === 'all' ? allStudents :
      filterQualified === 'qualified' ? allStudents.filter(s => s.qualified) :
      allStudents.filter(s => !s.qualified);
    
    // Then apply search filter if there's a search value
    if (allStudentsSearchValue) {
      const searchLower = allStudentsSearchValue.toLowerCase();
      filtered = filtered.filter(student => 
        (student.name && student.name.toLowerCase().includes(searchLower)) ||
        (student.uniqueId && student.uniqueId.toLowerCase().includes(searchLower)) ||
        (student.phone && student.phone.toLowerCase().includes(searchLower)) ||
        (student.fileId && student.fileId.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  };

  // Add a new method to add all qualified students from the filtered list
  const handleAddAllQualified = () => {
    const filteredStudents = getFilteredStudents();
    const qualifiedStudents = filteredStudents.filter(s => s.qualified);
    
    if (qualifiedStudents.length === 0) {
      message.warning('当前列表中没有符合条件的学员');
      return;
    }
    
    // Filter out students that are already selected
    const newStudents = qualifiedStudents.filter(
      s => !selectedStudents.some(selected => selected.objectId === s.objectId)
    );
    
    if (newStudents.length === 0) {
      message.info('所有符合条件的学员已在名单中');
      return;
    }
    
    // Add the new students
    onSelectedStudentsChange([...selectedStudents, ...newStudents]);
    message.success(`已添加 ${newStudents.length} 名符合条件的学员`);
  };

  // Add a function to check for duplicate examiners
  const checkDuplicateExaminers = (record) => {
    // Debug log to check the record data
    console.log('Checking duplicates for student:', record.name, record.exams);
    
    // Extract passed exams and their examiners
    const exams = record.exams || [];
    // Make sure we're comparing properly - convert examType to string if needed
    const passedExams = exams.filter(e => e.passed === true);
    console.log('Passed exams:', passedExams);
    
    // Group exams by examiner
    const examinerGroups = {};
    passedExams.forEach(exam => {
      if (exam.examiner) {
        if (!examinerGroups[exam.examiner]) {
          examinerGroups[exam.examiner] = [];
        }
        examinerGroups[exam.examiner].push(exam.examType);
      }
    });
    
    console.log('Examiner groups:', examinerGroups);
    
    // Check if any examiner has more than one exam
    const duplicateExaminers = Object.entries(examinerGroups)
      .filter(([_, types]) => types.length > 1)
      .map(([examiner, types]) => ({
        examiner,
        subjects: types.map(type => `科目${type}`).join('、')
      }));
    
    console.log('Duplicate examiners:', duplicateExaminers);
    return duplicateExaminers;
  };

  // Helper function to render the examiner with appropriate warning if needed
  const renderExaminer = (examiner, isDuplicate, duplicateInfo) => {
    if (!isDuplicate) {
      return <span>{examiner}</span>;
    }
    
    return (
      <Tooltip title={`注意: ${examiner} 同时出现在 ${duplicateInfo.subjects}`}>
        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          {examiner} <span style={{ marginLeft: 4 }}>⚠️</span>
        </span>
      </Tooltip>
    );
  };

  return (
    <Card>
      {/* Action Buttons */}
      <div style={{ 
        marginBottom: 24,
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <Button type="primary" onClick={() => setSearchModalVisible(true)}>
          添加学员
        </Button>
        <Button 
          onClick={fetchAllStudents}
          loading={loadingAllStudents}
        >
          查看所有可参加学员
        </Button>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Upload
            accept=".txt"
            beforeUpload={handleImport}
            showUploadList={false}
            disabled={importing}
          >
            <Button loading={importing}>
              {importing ? `导入中 ${Math.round(importProgress)}%` : '导入名单'}
            </Button>
          </Upload>
          {importing && (
            <Progress 
              percent={Math.round(importProgress)} 
              size="small" 
              style={{ 
                width: 100, 
                marginLeft: 8 
              }} 
            />
          )}
          <Popover
            title="导入说明"
            content={
              <div style={{ maxWidth: 300 }}>
                <p>文件要求：</p>
                <ul style={{ paddingLeft: 16, marginBottom: 8 }}>
                  <li>文件格式：.txt文本文件</li>
                  <li>文件内容：每行一个身份证号</li>
                </ul>
                <p>示例格式：</p>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 8, 
                  borderRadius: 4,
                  marginBottom: 8 
                }}>
                  {'123456789012345678\n234567890123456789\n345678901234567890'}
                </pre>
                <p>注意事项：</p>
                <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                  <li>确保身份证号格式正确</li>
                  <li>每个身份证号独占一行</li>
                  <li>不要包含其他特殊字符</li>
                </ul>
              </div>
            }
            trigger="hover"
            placement="right"
          >
            <QuestionCircleOutlined 
              style={{ 
                marginLeft: 8, 
                color: '#1890ff',
                cursor: 'pointer'
              }} 
            />
          </Popover>
        </div>
        <Button 
          onClick={() => examService.exportBookingToExcel(selectedStudents, bookingData)}
          disabled={selectedStudents.length === 0}
        >
          导出名单
        </Button>
      </div>

      {/* Search Modal */}
      <Modal
        title="添加学员"
        open={searchModalVisible}
        onCancel={() => {
          setSearchModalVisible(false);
          setSearchValue('');
          setSearchResults([]);
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ padding: '24px' }}
      >
        {/* Search Input */}
        <div style={{ 
          marginBottom: 24,
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <Input
            placeholder="输入学员姓名/身份证号/电话"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onPressEnter={handleSearch}
            style={{ flex: 1, minWidth: 200 }}
          />
          <Button 
            type="primary" 
            onClick={handleSearch}
            loading={searching}
          >
            搜索
          </Button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Table
            columns={[
              { title: '档案号', dataIndex: 'fileId', width: 100 },
              { title: '姓名', dataIndex: 'name', width: 100 },
              { title: '身份证号', dataIndex: 'uniqueId', width: 180 },
              { title: '电话', dataIndex: 'phone', width: 120 },
              { 
                title: '报名日期', 
                dataIndex: 'signUpDate', 
                width: 120,
                render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-'
              },
              { 
                title: 'VIP',
                dataIndex: 'VIP',
                width: 60,
                render: (vip) => vip ? '是' : '否'
              },
              { 
                title: '操作',
                key: 'action',
                width: 80,
                fixed: 'right',
                render: (_, record) => {
                  const isSelected = selectedStudents.some(s => s.objectId === record.objectId);
                  const { qualified, errors } = examService.checkStudentQualification(
                    record, 
                    bookingData.exam, 
                    bookingData.date
                  );

                  return (
                    <Tooltip 
                      title={!qualified ? errors.join('\n') : ''}
                      placement="left"
                    >
                      <Button 
                        type="link" 
                        onClick={() => handleAddStudent(record)}
                        disabled={isSelected || !qualified}
                        danger={!qualified}
                      >
                        添加
                      </Button>
                    </Tooltip>
                  );
                },
              },
            ]}
            dataSource={searchResults}
            rowKey="objectId"
            pagination={{
              pageSize: 5,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        )}
      </Modal>

      {/* All Students Modal */}
      <Modal
        title={`${bookingData.exam}考试 - 学员资格列表`}
        open={allStudentsModalVisible}
        onCancel={() => setAllStudentsModalVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: '24px' }}
      >
        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="学员筛选说明"
            description={`本列表仅显示尚未通过${bookingData.exam}考试的学员，已通过该考试的学员不会显示在此列表中。符合条件的学员表示已满足该考试的所有前置要求，可直接添加到考试名单。`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>
        
        {/* Filter Controls */}
        <div style={{ 
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Radio.Group 
            value={filterQualified} 
            onChange={e => setFilterQualified(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">全部学员</Radio.Button>
            <Radio.Button value="qualified">符合条件</Radio.Button>
            <Radio.Button value="unqualified">不符合条件</Radio.Button>
          </Radio.Group>
          <Button 
            type="primary" 
            onClick={handleAddAllQualified}
            icon={<PlusOutlined />}
          >
            一键添加所有符合条件学员
          </Button>
          <span style={{ margin: '0 8px' }}>
            总计: {allStudents.length} 人 | 
            符合条件: {allStudents.filter(s => s.qualified).length} 人 | 
            不符合条件: {allStudents.filter(s => !s.qualified).length} 人
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <Input.Search
              placeholder="搜索姓名/身份证号/电话/档案号"
              value={allStudentsSearchValue}
              onChange={e => setAllStudentsSearchValue(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Badge 
              status="processing" 
              text={`当前显示: ${getFilteredStudents().length} 名学员`} 
              style={{ marginLeft: 12 }}
            />
          </div>
        </div>

        {/* All Students Table */}
        <Table
          columns={[
            { 
              title: '备注',
              key: 'notes',
              width: 250,
              render: (_, record) => (
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {record.notes && record.notes.length > 0 ? (
                    <ul style={{ 
                      listStyleType: 'none', 
                      margin: 0, 
                      padding: 0 
                    }}>
                      {record.notes.map((note, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            {moment(note.date).format('YYYY-MM-DD HH:mm')}
                          </div>
                          <div>{note.content}</div>
                        </li>
                      ))}
                    </ul>
                  ) : <span style={{ color: '#999' }}>无备注</span>}
                </div>
              ),
            },
            { title: '档案号', dataIndex: 'fileId', width: 100 },
            { 
              title: '报名日期', 
              dataIndex: 'signUpDate', 
              width: 120,
              render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-'
            },
            { 
              title: '姓名', 
              dataIndex: 'name', 
              width: 100,
              render: (text, record) => (
                <Button 
                  type="link" 
                  onClick={() => {
                    setCurrentStudent(record);
                    setDetailModalVisible(true);
                  }}
                  style={{ padding: 0 }}
                >
                  {text}
                </Button>
              )
            },
            { title: '身份证号', dataIndex: 'uniqueId', width: 180 },
            { title: '电话', dataIndex: 'phone', width: 120 },
            { title: '地址', dataIndex: 'address', width: 200 },
            { title: '推荐人', dataIndex: 'referrer', width: 100 },
            {
              title: '学费',
              dataIndex: 'tuitionFee',
              width: 100,
              render: (fee) => fee ? `¥${fee}` : '-'
            },
            { 
              title: 'VIP',
              dataIndex: 'VIP',
              width: 60,
              render: (vip) => vip ? '是' : '否'
            },
            { 
              title: '科目一',
              dataIndex: 'exam1',
              width: 120,
              render: (exam, record) => {
                if (!exam) return '-';
                
                // Get examiner from exams array if available
                const exams = record.exams || [];
                
                // Try multiple ways to find the matching exam
                let passedExam = null;
                
                // Method 1: String comparison
                passedExam = exams.find(e => String(e.examType) === '1' && e.passed === true);
                
                // Method 2: Loose comparison if Method 1 fails
                if (!passedExam) {
                  passedExam = exams.find(e => e.examType == 1 && e.passed);
                }
                
                // Method 3: Just match the type if still not found
                if (!passedExam) {
                  const matchingExams = exams.filter(e => String(e.examType) === '1' || e.examType == 1);
                  const passedMatchingExams = matchingExams.filter(e => e.passed === true || e.passed === 'true' || e.passed);
                  passedExam = passedMatchingExams[0];
                }
                
                // Use the examiner from the found exam, or fall back to the main exam object
                const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
                
                if (exam.passed) {
                  // Check for duplicate examiners
                  const duplicates = checkDuplicateExaminers(record);
                  const isDuplicate = duplicates.some(d => d.examiner === examiner);
                  const duplicateInfo = isDuplicate ? duplicates.find(d => d.examiner === examiner) : null;
                  
                  return (
                    <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                      <Badge status="success" text={renderExaminer(examiner, isDuplicate, duplicateInfo)} />
                    </Tooltip>
                  );
                } else {
                  // Count failures from exams array
                  const failures = exams.filter(e => String(e.examType) === '1' && e.passed === false).length;
                  return (
                    <Tooltip title={`已失败 ${failures} 次，剩余 ${10 - failures} 次机会`}>
                      <Badge status="error" text={`失败 ${failures} 次`} />
                    </Tooltip>
                  );
                }
              }
            },
            { 
              title: '科目二',
              dataIndex: 'exam2',
              width: 120,
              render: (exam, record) => {
                if (!exam) return '-';
                
                // Get examiner from exams array if available
                const exams = record.exams || [];
                const passedExam = exams.find(e => String(e.examType) === '2' && e.passed === true);
                
                // Use the examiner from the found exam, or fall back to the main exam object
                const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
                
                if (exam.passed) {
                  // Check for duplicate examiners
                  const duplicates = checkDuplicateExaminers(record);
                  const isDuplicate = duplicates.some(d => d.examiner === examiner);
                  const duplicateInfo = isDuplicate ? duplicates.find(d => d.examiner === examiner) : null;
                  
                  return (
                    <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                      <Badge status="success" text={renderExaminer(examiner, isDuplicate, duplicateInfo)} />
                    </Tooltip>
                  );
                } else {
                  // Count failures from exams array
                  const failures = exams.filter(e => String(e.examType) === '2' && e.passed === false).length;
                  return (
                    <Tooltip title={`已失败 ${failures} 次，剩余 ${5 - failures} 次机会`}>
                      <Badge status="error" text={`失败 ${failures} 次`} />
                    </Tooltip>
                  );
                }
              }
            },
            { 
              title: '科目三',
              dataIndex: 'exam3',
              width: 120,
              render: (exam, record) => {
                if (!exam) return '-';
                
                // Get examiner from exams array if available
                const exams = record.exams || [];
                const passedExam = exams.find(e => String(e.examType) === '3' && e.passed === true);
                
                // Use the examiner from the found exam, or fall back to the main exam object
                const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
                
                if (exam.passed) {
                  // Check for duplicate examiners
                  const duplicates = checkDuplicateExaminers(record);
                  const isDuplicate = duplicates.some(d => d.examiner === examiner);
                  const duplicateInfo = isDuplicate ? duplicates.find(d => d.examiner === examiner) : null;
                  
                  return (
                    <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                      <Badge status="success" text={renderExaminer(examiner, isDuplicate, duplicateInfo)} />
                    </Tooltip>
                  );
                } else {
                  // Count failures from exams array
                  const failures = exams.filter(e => String(e.examType) === '3' && e.passed === false).length;
                  return (
                    <Tooltip title={`已失败 ${failures} 次，剩余 ${5 - failures} 次机会`}>
                      <Badge status="error" text={`失败 ${failures} 次`} />
                    </Tooltip>
                  );
                }
              }
            },
            { 
              title: '科目四',
              dataIndex: 'exam4',
              width: 120,
              render: (exam, record) => {
                if (!exam) return '-';
                
                // Get examiner from exams array if available
                const exams = record.exams || [];
                const passedExam = exams.find(e => String(e.examType) === '4' && e.passed === true);
                
                // Use the examiner from the found exam, or fall back to the main exam object
                const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
                
                if (exam.passed) {
                  // Check for duplicate examiners
                  const duplicates = checkDuplicateExaminers(record);
                  const isDuplicate = duplicates.some(d => d.examiner === examiner);
                  const duplicateInfo = isDuplicate ? duplicates.find(d => d.examiner === examiner) : null;
                  
                  return (
                    <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                      <Badge status="success" text={renderExaminer(examiner, isDuplicate, duplicateInfo)} />
                    </Tooltip>
                  );
                } else {
                  // Count failures from exams array
                  const failures = exams.filter(e => String(e.examType) === '4' && e.passed === false).length;
                  return (
                    <Tooltip title={`已失败 ${failures} 次，剩余 ${10 - failures} 次机会`}>
                      <Badge status="error" text={`失败 ${failures} 次`} />
                    </Tooltip>
                  );
                }
              }
            },
            {
              title: '审核状态',
              dataIndex: 'verified',
              width: 90,
              render: (verified) => 
                verified ? <Badge status="success" text="已审核" /> : <Badge status="error" text="未审核" />
            },
            {
              title: '资格',
              dataIndex: 'qualified',
              width: 100,
              render: (qualified, record) => (
                qualified ? 
                  <Badge status="success" text="符合条件" /> : 
                  <Tooltip title={record.errors.join('\n')} placement="left">
                    <Badge status="error" text="不符合条件" />
                  </Tooltip>
              )
            },
            {
              title: '不符合原因',
              dataIndex: 'errors',
              width: 200,
              render: (errors) => 
                errors && errors.length > 0 ? (
                  <ul style={{ 
                    listStyleType: 'none', 
                    margin: 0, 
                    padding: 0 
                  }}>
                    {errors.map((error, index) => (
                      <li key={index} style={{ color: '#ff4d4f' }}>{error}</li>
                    ))}
                  </ul>
                ) : null
            },
            {
              title: '操作',
              key: 'action',
              width: 80,
              fixed: 'right',
              render: (_, record) => {
                const isSelected = selectedStudents.some(s => s.objectId === record.objectId);
                
                return (
                  <Button 
                    type="link" 
                    onClick={() => handleAddStudentFromList(record)}
                    disabled={isSelected || !record.qualified}
                    danger={!record.qualified}
                  >
                    {isSelected ? '已选择' : '添加'}
                  </Button>
                );
              },
            },
          ]}
          dataSource={getFilteredStudents()}
          rowKey="objectId"
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(90vh - 300px)' }}
          loading={loadingAllStudents}
          size="small"
        />
      </Modal>

      {/* Notes Modal */}
      <Modal
        title={`${currentStudentName}的备注`}
        open={notesModalVisible}
        onCancel={() => setNotesModalVisible(false)}
        footer={null}
        width={600}
      >
        {currentNotes.length > 0 ? (
          <Timeline>
            {currentNotes.map((note, index) => (
              <Timeline.Item key={index}>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}>
                  <div style={{ marginBottom: '4px', color: '#666' }}>
                    {moment(note.date).format('YYYY-MM-DD HH:mm')}
                  </div>
                  <div>{note.content}</div>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Empty description="暂无备注" />
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="学员详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        {currentStudent && <StudentDetail student={currentStudent} />}
      </Modal>

      {/* Selected Students Table */}
      <Table
        columns={[
          { 
            title: '备注',
            key: 'notes',
            width: 250,
            render: (_, record) => (
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {record.notes && record.notes.length > 0 ? (
                  <ul style={{ 
                    listStyleType: 'none', 
                    margin: 0, 
                    padding: 0 
                  }}>
                    {record.notes.map((note, index) => (
                      <li key={index} style={{ marginBottom: '4px' }}>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          {moment(note.date).format('YYYY-MM-DD HH:mm')}
                        </div>
                        <div>{note.content}</div>
                      </li>
                    ))}
                  </ul>
                ) : <span style={{ color: '#999' }}>无备注</span>}
              </div>
            ),
          },
          { title: '档案号', dataIndex: 'fileId', width: 100 },
          { 
            title: '报名日期', 
            dataIndex: 'signUpDate', 
            width: 120,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-'
          },
          { 
            title: '姓名', 
            dataIndex: 'name', 
            width: 100,
            render: (text, record) => (
              <Button 
                type="link" 
                onClick={() => handleViewDetail(record)}
                style={{ padding: 0 }}
              >
                {text}
              </Button>
            )
          },
          { title: '身份证号', dataIndex: 'uniqueId', width: 180 },
          { title: '电话', dataIndex: 'phone', width: 120 },
          { title: '地址', dataIndex: 'address', width: 200 },
          { title: '推荐人', dataIndex: 'referrer', width: 100 },
          {
            title: '学费',
            dataIndex: 'tuitionFee',
            width: 100,
            render: (fee) => fee ? `¥${fee}` : '-'
          },
          { 
            title: 'VIP',
            dataIndex: 'VIP',
            width: 60,
            render: (vip) => vip ? '是' : '否'
          },
          { 
            title: '科目一',
            dataIndex: 'exam1',
            width: 120,
            render: (exam, record) => {
              if (!exam) return '-';
              
              // Get examiner from exams array if available
              const exams = record.exams || [];
              
              // Try multiple ways to find the matching exam
              let passedExam = null;
              passedExam = exams.find(e => String(e.examType) === '1' && e.passed === true);
              if (!passedExam) {
                passedExam = exams.find(e => e.examType == 1 && e.passed);
              }
              
              // Use the examiner from the found exam, or fall back to the main exam object
              const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
              
              if (exam.passed) {
                // Check for duplicate examiners
                const duplicates = checkDuplicateExaminers(record);
                const isDuplicate = duplicates.some(d => d.examiner === examiner);
                const duplicateInfo = isDuplicate ? duplicates.find(d => d.examiner === examiner) : null;
                
                return (
                  <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                    <Badge status="success" text={renderExaminer(examiner, isDuplicate, duplicateInfo)} />
                  </Tooltip>
                );
              } else {
                // Count failures from exams array
                const failures = exams.filter(e => String(e.examType) === '1' && e.passed === false).length;
                return (
                  <Tooltip title={`已失败 ${failures} 次，剩余 ${10 - failures} 次机会`}>
                    <Badge status="error" text={`失败 ${failures} 次`} />
                  </Tooltip>
                );
              }
            }
          },
          { 
            title: '科目二',
            dataIndex: 'exam2',
            width: 120,
            render: (exam, record) => {
              if (!exam) return '-';
              
              // Get examiner from exams array if available
              const exams = record.exams || [];
              const passedExam = exams.find(e => String(e.examType) === '2' && e.passed === true);
              
              // Use the examiner from the found exam, or fall back to the main exam object
              const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
              
              if (exam.passed) {
                return (
                  <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                    <Badge status="success" text={examiner} />
                  </Tooltip>
                );
              } else {
                // Count failures from exams array
                const failures = exams.filter(e => String(e.examType) === '2' && e.passed === false).length;
                return (
                  <Tooltip title={`已失败 ${failures} 次，剩余 ${5 - failures} 次机会`}>
                    <Badge status="error" text={`失败 ${failures} 次`} />
                  </Tooltip>
                );
              }
            }
          },
          { 
            title: '科目三',
            dataIndex: 'exam3',
            width: 120,
            render: (exam, record) => {
              if (!exam) return '-';
              
              // Get examiner from exams array if available
              const exams = record.exams || [];
              const passedExam = exams.find(e => String(e.examType) === '3' && e.passed === true);
              
              // Use the examiner from the found exam, or fall back to the main exam object
              const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
              
              if (exam.passed) {
                return (
                  <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                    <Badge status="success" text={examiner} />
                  </Tooltip>
                );
              } else {
                // Count failures from exams array
                const failures = exams.filter(e => String(e.examType) === '3' && e.passed === false).length;
                return (
                  <Tooltip title={`已失败 ${failures} 次，剩余 ${5 - failures} 次机会`}>
                    <Badge status="error" text={`失败 ${failures} 次`} />
                  </Tooltip>
                );
              }
            }
          },
          { 
            title: '科目四',
            dataIndex: 'exam4',
            width: 120,
            render: (exam, record) => {
              if (!exam) return '-';
              
              // Get examiner from exams array if available
              const exams = record.exams || [];
              const passedExam = exams.find(e => String(e.examType) === '4' && e.passed === true);
              
              // Use the examiner from the found exam, or fall back to the main exam object
              const examiner = passedExam ? passedExam.examiner : (exam.examiner || '未知考官');
              
              if (exam.passed) {
                return (
                  <Tooltip title={`通过日期: ${moment(exam.date).format('YYYY-MM-DD')}`}>
                    <Badge status="success" text={examiner} />
                  </Tooltip>
                );
              } else {
                // Count failures from exams array
                const failures = exams.filter(e => String(e.examType) === '4' && e.passed === false).length;
                return (
                  <Tooltip title={`已失败 ${failures} 次，剩余 ${10 - failures} 次机会`}>
                    <Badge status="error" text={`失败 ${failures} 次`} />
                  </Tooltip>
                );
              }
            }
          },
          {
            title: '操作',
            key: 'action',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
              <Button 
                type="link" 
                danger 
                onClick={() => handleRemoveStudent(record.objectId)}
              >
                删除
              </Button>
            ),
          },
        ]}
        dataSource={selectedStudents}
        rowKey="objectId"
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize: 10,
          showTotal: (total) => `共 ${total} 条记录`
        }}
      />

      {/* Exam Info Summary */}
      <Card
        style={{ marginTop: 24 }}
        size="small"
        title="当前考试信息"
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16
        }}>
          <div>考试科目：{bookingData.exam}</div>
          <div>考试日期：{bookingData.date?.format('YYYY-MM-DD')}</div>
          <div>考官：{bookingData.examiner}</div>
          <div>已选学员数：{selectedStudents.length}</div>
        </div>
      </Card>
    </Card>
  );
};

const ExamBooking = ({ onNavigate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [bookingData, setBookingData] = useState({
    exam: null,
    date: null,
    studentCount: 0,
    examiner: '',
  });
  const [qualifiedStudents, setQualifiedStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Persist booking data in localStorage
  useEffect(() => {
    // Load saved data when component mounts
    const savedData = localStorage.getItem('examBookingData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      // Convert date string back to moment object if it exists
      if (parsed.date) {
        parsed.date = moment(parsed.date);
      }
      setBookingData(parsed);
    }
  }, []);

  useEffect(() => {
    // Save data whenever it changes
    localStorage.setItem('examBookingData', JSON.stringify(bookingData));
  }, [bookingData]);

  const fetchQualifiedStudents = async () => {
    try {
      setLoading(true);
      const { exam, date, studentCount } = bookingData;
      if (!exam || !date || !studentCount) {
        message.error('请填写完整信息');
        return;
      }

      const students = await examService.getQualifiedStudents({
        exam,
        date,
        count: studentCount
      });

      setQualifiedStudents(students);
      setSelectedStudents(students.slice(0, studentCount));
      setCurrentStep(2);
    } catch (error) {
      message.error('获取符合条件的学员失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!bookingData.date || !bookingData.exam) {
        message.error('请填写完整预约信息');
        return;
      }

      if (selectedStudents.length === 0) {
        message.error('请选择至少一名学员');
        return;
      }

      setSubmitting(true);
      const result = await examService.submitExamBooking(bookingData, selectedStudents);
      message.success('预约提交成功');
      
      // Clear localStorage
      localStorage.removeItem('examBookingData');
      
      // Navigate to exam history with the booking ID
      if (onNavigate) {
        onNavigate('examHistory', { bookingId: result.objectId });
      }
      
    } catch (error) {
      console.error('Submit booking error:', error);
      message.error('预约提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      title: '选择考试科目',
      content: <ExamSelectionStep 
        value={bookingData.exam}
        onChange={(exam) => setBookingData(prev => ({ ...prev, exam }))}
      />,
    },
    {
      title: '选择考试日期',
      content: <DateSelectionStep 
        bookingData={bookingData}
        onBookingDataChange={setBookingData}
      />,
    },
    {
      title: '确认考试名单',
      content: <StudentSelectionStep 
        bookingData={bookingData}
        selectedStudents={selectedStudents}
        onSelectedStudentsChange={setSelectedStudents}
        qualifiedStudents={qualifiedStudents}
      />,
    },
  ];

  return (
    <Card title="考试预约">
      <Steps current={currentStep} items={steps} />
      <div style={{ marginTop: 24 }}>
        {steps[currentStep].content}
      </div>
      <div style={{ marginTop: 24 }}>
        {currentStep > 0 && (
          <Button style={{ margin: '0 8px' }} onClick={() => setCurrentStep(prev => prev - 1)}>
            上一步
          </Button>
        )}
        {currentStep < steps.length - 1 && (
          <Button 
            type="primary" 
            onClick={() => {
              if (currentStep === 1) {
                fetchQualifiedStudents();
              } else {
                setCurrentStep(prev => prev + 1);
              }
            }}
            loading={loading}
          >
            下一步
          </Button>
        )}
        {currentStep === steps.length - 1 && (
          <Button 
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!bookingData.date || !bookingData.exam || selectedStudents.length === 0 || submitting}
          >
            {submitting ? '提交中...' : '提交预约'}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default ExamBooking; 